import { FaShieldAlt, FaUserLock, FaDatabase, FaEnvelope, FaCookie, FaGlobe } from 'react-icons/fa';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen mb-4">
      {/* Header */}
      <header className="border-b border-gray-200">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="text-3xl font-bold text-gray-800">
              <span className="text-[#FD5F20]">ATO</span> Creative
            </div>
            <div className="text-sm text-gray-500">Last Updated: December 1, 2024</div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#FD5F20] text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-16">
        <div className="max-w-4xl mx-auto prose prose-lg">
          {/* Introduction */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaShieldAlt className="text-[#FD5F20] mr-3" />
              Introduction
            </h2>
            <p className="text-gray-600 mb-4">
              ATO Creative Digital Marketing Agency ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website atocreative.et or use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaDatabase className="text-[#FD5F20] mr-3" />
              Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Personal Information</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>Name, email address, phone number</li>
              <li>Business information and contact details</li>
              <li>Billing and payment information</li>
              <li>Communication preferences</li>
              <li>Any information you provide when contacting us</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-4">Automatically Collected Information</h3>
            <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on site</li>
              <li>Referring website and search terms</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaUserLock className="text-[#FD5F20] mr-3" />
              How We Use Your Information
            </h2>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Provide, operate, and maintain our services</li>
              <li>Process your transactions and send related information</li>
              <li>Respond to your comments and questions</li>
              <li>Send you technical notices and support messages</li>
              <li>Communicate about promotions and upcoming events (with your consent)</li>
              <li>Monitor and analyze trends and usage</li>
              <li>Improve our website and services</li>
              <li>Protect against fraudulent or illegal activity</li>
            </ul>
          </section>

          {/* Cookies and Tracking */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaCookie className="text-[#FD5F20] mr-3" />
              Cookies and Tracking Technologies
            </h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar tracking technologies to track activity on our website and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier.
            </p>
            <p className="text-gray-600">
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">
              We may share information we have collected about you in certain situations:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Service Providers:</strong> With trusted third parties who assist us in operating our website and services</li>
              <li><strong>Business Transfers:</strong> In connection with any merger or sale of company assets</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>With Your Consent:</strong> When you give us explicit permission to share your information</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Data Retention</h2>
            <p className="text-gray-600">
              We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our policies.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Privacy Rights</h2>
            <p className="text-gray-600 mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify or update inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Restrict or object to our processing of your personal data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
          </section>

          {/* International Transfers */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaGlobe className="text-[#FD5F20] mr-3" />
              International Data Transfers
            </h2>
            <p className="text-gray-600">
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-16 bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaEnvelope className="text-[#FD5F20] mr-3" />
              Contact Us
            </h2>
            <p className="text-gray-600 mb-6">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <p className="text-gray-800 font-semibold mb-2">ATO Creative Digital Marketing Agency</p>
              <p className="text-gray-600 mb-1">Email: contact@atocreative.et</p>
              <p className="text-gray-600 mb-1">Phone: +251 71 569 6893</p>
              <p className="text-gray-600">Address: Addi Ababa, Ethiopia</p>
            </div>
          </section>

          {/* Policy Updates */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Changes to This Privacy Policy</h2>
            <p className="text-gray-600">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>
        </div>
      </main>

      <footer className="bg-black py-8">
        <div className="container mx-auto px-6 text-center">
          <p className="text-[#FD5F20]">
            © {new Date().getFullYear()} ATO Creative Digital Marketing Agency. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}